import { AlertSeverityType } from '@/types/alerts';

/**
 * Generates a human-readable message for an alert
 */
export function generateAlertMessage(alert: any): string {
    if (!alert) return 'Unknown alert';

    // Build a descriptive message
    const deviceName = alert.device?.name || alert.device?.serial_number || 'Unknown device';
    const sensorName = alert.sensor?.name || alert.sensor?.type || 'sensor';
    const value = typeof alert.triggered_value === 'number'
        ? alert.triggered_value.toFixed(2)
        : JSON.stringify(alert.triggered_value);
    const unit = alert.sensor?.unit || '';

    if (alert.alert_rule?.condition_type && alert.alert_rule?.threshold_value !== undefined) {
        const condition = alert.alert_rule.condition_type;
        const threshold = alert.alert_rule.threshold_value;

        switch (condition) {
            case 'gt':
                return `${sensorName} on ${deviceName} exceeded threshold (${value}${unit} > ${threshold}${unit})`;
            case 'lt':
                return `${sensorName} on ${deviceName} below threshold (${value}${unit} < ${threshold}${unit})`;
            case 'eq':
                return `${sensorName} on ${deviceName} equals threshold (${value}${unit} = ${threshold}${unit})`;
            case 'between':
            case 'range':
                if (Array.isArray(threshold) && threshold.length >= 2) {
                    return `${sensorName} on ${deviceName} in range of ${threshold[0]}${unit} - ${threshold[1]}${unit} (${value}${unit})`;
                }
                break;
            case 'outside':
                if (Array.isArray(threshold) && threshold.length >= 2) {
                    return `${sensorName} on ${deviceName} outside range (${value}${unit} not in ${threshold[0]}${unit} - ${threshold[1]}${unit})`;
                }
                break;
        }
    }

    // Generic fallback
    return `Alert for ${sensorName} on ${deviceName}: ${value}${unit}`;
}

/**
 * Gets a color class based on alert severity
 */
export const getAlertSeverityColorClasses = (
    severity: AlertSeverityType
): { bgLight: string; bgDark: string; textLight: string; textDark: string; badgeColor: string } => {
    switch (severity) {
        case 'critical':
            return {
                bgLight: 'bg-red-100',
                bgDark: 'dark:bg-red-950/50',
                textLight: 'text-red-600',
                textDark: 'dark:text-red-400',
                badgeColor: 'red'
            };
        case 'high':
            return {
                bgLight: 'bg-orange-100',
                bgDark: 'dark:bg-orange-950/50',
                textLight: 'text-orange-600',
                textDark: 'dark:text-orange-400',
                badgeColor: 'orange'
            };
        case 'medium':
            return {
                bgLight: 'bg-yellow-100',
                bgDark: 'dark:bg-yellow-950/50',
                textLight: 'text-yellow-600',
                textDark: 'dark:text-yellow-400',
                badgeColor: 'yellow'
            };
        case 'low':
            return {
                bgLight: 'bg-blue-100',
                bgDark: 'dark:bg-blue-950/50',
                textLight: 'text-blue-600',
                textDark: 'dark:text-blue-400',
                badgeColor: 'blue'
            };
        case 'info':
            return {
                bgLight: 'bg-green-100',
                bgDark: 'dark:bg-green-950/50',
                textLight: 'text-green-600',
                textDark: 'dark:text-green-400',
                badgeColor: 'green'
            };
        default:
            return {
                bgLight: 'bg-gray-100',
                bgDark: 'dark:bg-gray-800/50',
                textLight: 'text-gray-600',
                textDark: 'dark:text-gray-400',
                badgeColor: 'gray'
            };
    }
};

/**
 * Gets status badge color
 */
export const getAlertStatusColorClasses = (
    status: string
): { bg: string; text: string } => {
    switch (status) {
        case 'active':
            return {
                bg: 'bg-red-100 dark:bg-red-950/30',
                text: 'text-red-700 dark:text-red-400'
            };
        case 'acknowledged':
            return {
                bg: 'bg-yellow-100 dark:bg-yellow-950/30',
                text: 'text-yellow-700 dark:text-yellow-400'
            };
        case 'resolved':
            return {
                bg: 'bg-green-100 dark:bg-green-950/30',
                text: 'text-green-700 dark:text-green-400'
            };
        default:
            return {
                bg: 'bg-gray-100 dark:bg-gray-800/30',
                text: 'text-gray-700 dark:text-gray-400'
            };
    }
};
